import { createContext, type ReactNode, useContext, useMemo, useRef } from "react"

import { withActionObservers } from "./action-observer"
import {
  type ActionCatalogEntry,
  createActionCatalog,
  drillToAction,
  setStateAction,
} from "./actions-catalog"
import { type DashboardStateStore, useDashboardStateStore } from "../state/dashboard-state"
import { withViewTransition } from "../utils/view-transition"

/**
 * Concrete handler signature. Receives validated params plus the live
 * runtime context (state store + caller-supplied extras).
 */
export type ActionHandler = (
  params: Record<string, unknown>,
  ctx: ActionDispatchContext
) => unknown | Promise<unknown>

/**
 * Runtime context passed to every handler. Extension point — host apps
 * can extend by augmenting the type and supplying handlers that read
 * additional fields.
 */
export interface ActionDispatchContext {
  /** Live dashboard state store. */
  stateStore: DashboardStateStore
}

interface ActionsRegistryValue {
  /** Look up the catalog declaration for an action. */
  describe(name: string): ActionCatalogEntry | undefined
  /** Dispatch by name. Wraps the handler in action-observer events. */
  dispatch(
    name: string,
    params?: Record<string, unknown>
  ): Promise<{ ok: true; result: unknown } | { ok: false; error: unknown }>
}

const ActionsRegistryContext = createContext<ActionsRegistryValue | null>(null)

interface ActionsRegistryProviderProps {
  children: ReactNode
  /**
   * Catalog declarations available to this provider. Defaults to the
   * standard set; pass a customised list to expose additional named
   * actions to the AI prompt.
   */
  catalog?: ActionCatalogEntry[]
  /**
   * Handler implementations keyed by action name. `setState` is wired
   * by default; override or extend by passing your own map.
   */
  handlers?: Record<string, ActionHandler>
}

/**
 * Provider for the action registry. Mount once near the top of a Dash
 * dashboard subtree (typically inside DashboardStateProvider so `setState`
 * has somewhere to write).
 */
export function ActionsRegistryProvider({
  children,
  catalog,
  handlers,
}: ActionsRegistryProviderProps) {
  const stateStore = useDashboardStateStore()
  const catalogMapRef = useRef(createActionCatalog(catalog))

  // Merge built-in handlers with caller overrides. Caller wins on
  // conflict so a host app can swap implementations (e.g. replace
  // `chat` with their own composer).
  const handlerMap = useMemo<Record<string, ActionHandler>>(() => {
    return {
      [setStateAction.name]: defaultSetStateHandler,
      ...(handlers ?? {}),
    }
  }, [handlers])

  const value = useMemo<ActionsRegistryValue>(() => {
    const catalogMap = catalogMapRef.current
    return {
      describe: (name) => catalogMap.get(name),
      async dispatch(name, params) {
        const entry = catalogMap.get(name)
        const handler = handlerMap[name]
        const safeParams = params ?? {}

        // Validate params if the catalog declared a schema. Validation
        // failure throws — surface as a settled "fail" via the wrapper.
        if (entry) {
          const parse = entry.paramsSchema.safeParse(safeParams)
          if (!parse.success) {
            return {
              ok: false,
              error: new Error(`Action ${name} received invalid params: ${parse.error.message}`),
            }
          }
        }

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!handler) {
          // No implementation registered. Don't throw — emit a settled
          // "fail" so devtools and the action observer see it, and let
          // the caller decide what to do.
          return {
            ok: false,
            error: new Error(`No handler registered for action "${name}"`),
          }
        }

        try {
          // The drillTo action is the canonical "dashboard navigation
          // change" — by definition the user expects the swap to feel
          // animated rather than abrupt. Wrap the handler in a view
          // transition so any host-registered drillTo handler gets the
          // browser cross-fade for free, without each host having to
          // know about the View Transitions API. Other actions skip the
          // wrapper because their state changes (setState, exportCsv,
          // refreshBinding, chat) shouldn't visually transition the
          // entire viewport.
          const wrapped: () => unknown | Promise<unknown> =
            name === drillToAction.name
              ? () =>
                  new Promise<unknown>((resolve, reject) => {
                    let captured: unknown
                    let rejected: unknown | null = null
                    withViewTransition(`dash-${name}`, async () => {
                      try {
                        captured = await handler(safeParams, { stateStore })
                      } catch (err) {
                        rejected = err ?? new Error("drillTo handler threw")
                      }
                    })
                      .then(() => {
                        if (rejected !== null) reject(rejected)
                        else resolve(captured)
                      })
                      .catch(reject)
                  })
              : () => handler(safeParams, { stateStore })

          const result = await withActionObservers(name, safeParams, wrapped)
          return { ok: true, result }
        } catch (error) {
          return { ok: false, error }
        }
      },
    }
  }, [handlerMap, stateStore])

  return <ActionsRegistryContext.Provider value={value}>{children}</ActionsRegistryContext.Provider>
}

/**
 * Read the active actions registry. Returns `null` outside of the
 * provider (defensive — components that need to dispatch should
 * always be rendered inside).
 */
export function useActionsRegistry(): ActionsRegistryValue | null {
  return useContext(ActionsRegistryContext)
}

// ── Default handlers ───────────────────────────────────────────────

/**
 * Default implementation of the `setState` action.
 * Exported (and unit-tested separately) so the host app can wrap or
 * replace it without losing the canonical behaviour.
 */
export function defaultSetStateHandler(
  params: Record<string, unknown>,
  ctx: ActionDispatchContext
): null {
  const path = typeof params.path === "string" ? params.path : null
  if (!path) {
    throw new Error("setState requires a string `path` param")
  }
  ctx.stateStore.setDashState(path, params.value)
  return null
}
